import { isArticleSection } from '#pplx/frontend/apps/ask/articles/articles-utils/ArticleUtils.js';
import { parseFinanceRoute } from '#pplx/frontend/apps/ask/finance/routes.js';
import { logWithTime, warnWithTime } from '#pplx/frontend/apps/ask/o11y/logging.js';
import { canonicalizeLegacyPermalinkUrlText } from '#pplx/frontend/apps/ask/permalink-slug/canonicalizeLegacyPermalink.js';
import { mayRenderUserContentInOG } from '#pplx/frontend/apps/ask/spa/metadata/ogUserContentAccess.js';
import { cleanPPLXActionLinks } from '#pplx/frontend/components/search/markdown/utils/cleanPPLXActionLinks.js';
import { extractQuoteFromQuery } from '#pplx/frontend/components/search/markdown/utils/quoteUtils.js';
import { PERPLEXITY_DEFAULT_SHARE_LOGO_URL, PERPLEXITY_PRO_SUMMARY_SHARE_URL, } from '#pplx/frontend/components/utils/consts.js';
import { MessageHelper } from '#pplx/frontend/components/utils/MessageHelper.js';
import { truncateText } from '#pplx/frontend/composer-sdk/lexical/utils/truncateText.js';
import { r2Asset } from '#pplx/frontend/r2-assets/r2Asset.js';
import { isWindowsApp } from '#pplx/frontend/user-agent-parser/browser.js';
import { browserInvitesClient } from '#pplx_generated/typespec/rest/browser/invites/client.js';
const DEFAULT_TITLE = 'Perplexity';
export const PERPLEXITY_SITE_URL = 'https://www.perplexity.ai';
const TITLE_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 160;
export const DEFAULT_FAVICON_HREF = '/favicon.svg';
export const COMPUTER_FAVICON_HREF = '/favicon-computer.svg';
export const COMPUTER_PAGE_TITLE = 'Perplexity Computer';
export const LIBRARY_PAGE_TITLE = 'Perplexity Library';
export const SPACES_PAGE_TITLE = 'Perplexity Spaces';
export const PROJECTS_PAGE_TITLE = 'Perplexity Projects';
export const COMPUTER_PAGE_DESCRIPTION = 'Everything AI can do, Perplexity Computer does for you. Computer works on any task: building websites, editing documents, analyzing data, and automating repetitive work.';
export const COMPUTER_PAGE_IMAGE_URL = 'https://r2cdn.perplexity.ai/computer/og.webp';
// FIXME: i18n this description somehow
const DEFAULT_DESCRIPTION = 'Perplexity is a free AI-powered answer engine that provides accurate, trusted, and real-time answers to any question.';
function YYYYMMDD(dateStr) {
    if (!dateStr) {
        return null;
    }
    try {
        return new Date(dateStr).toISOString().slice(0, 10);
    }
    catch {
        return null;
    }
}
export const DEFAULT_METADATA = {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    metadataBase: new URL('https://www.perplexity.ai'),
    icons: {
        icon: DEFAULT_FAVICON_HREF,
    },
    openGraph: {
        images: [PERPLEXITY_DEFAULT_SHARE_LOGO_URL],
        locale: 'en',
        siteName: 'Perplexity AI',
        type: 'website',
        url: 'https://www.perplexity.ai',
    },
};
const isArticlePage = (entity) => MessageHelper.isArticleMode(entity) || entity.mode?.toLowerCase() === 'article';
export const generateThreadPageMetadataSync = ({ firstEntity, slug, userAgent, audience, }) => {
    // Legal (2026-08-12): user content only in an OG preview for an explicit
    // public share. Everything below derives from the thread, so gate the lot
    // rather than field by field. Fails closed on a missing level.
    //
    // Must precede the Windows-app branch: `userAgent` is caller-supplied and
    // `/api/bot-previews/*` is unauthenticated, so a claimed Windows UA would
    // otherwise take an earlier return and skip the gate.
    if (audience === 'crawler' && !mayRenderUserContentInOG(firstEntity)) {
        // Result only: this tracker logs `slug`/`userAgent` to Datadog, and a legacy
        // permalink embeds the title. The call sites below are past the gate, or
        // in-app for the user's own thread.
        trackThreadOGImageServing({ result: 'not_public_share' });
        return generatePageMetadataSync({
            index: false,
            userAgent,
            gatedUserContent: true,
        });
    }
    // While we wait for https://github.com/electron/electron/issues/45958 to get resolved, this is a workaround to keep a consistent title for the Windows app with a non-transparent titlebar
    const windowsApp = userAgent ? isWindowsApp(userAgent) : false;
    if (windowsApp) {
        return generatePageMetadataSync({
            title: DEFAULT_TITLE,
            index: false,
            userAgent,
        });
    }
    if (!firstEntity) {
        trackThreadOGImageServing({ result: 'no_result', slug, userAgent });
        // No title rather than the raw query: this metadata is served to crawlers
        // (including `facebookexternalhit`), and the query is the user's own words.
        return generatePageMetadataSync({
            index: false,
            userAgent,
        });
    }
    const parsed = MessageHelper.parseAskTextField(firstEntity);
    if (!parsed) {
        trackThreadOGImageServing({ result: 'bad_parse', slug, userAgent });
        return generatePageMetadataSync({
            index: false,
            userAgent,
        });
    }
    const imageURL = getThreadOGImageURL(firstEntity);
    trackThreadOGImageServing({
        result: 'url_served',
        slug,
        userAgent,
        imageURL,
    });
    let title = DEFAULT_TITLE;
    let readTime;
    let author;
    if (isArticleSection(firstEntity) && firstEntity.article_info?.title) {
        title = firstEntity.article_info.title;
        readTime = firstEntity?.article_info?.read_time;
        author = firstEntity?.author_username;
    }
    else if (firstEntity.thread_title) {
        title = firstEntity.thread_title;
    }
    // No `query_str` fallback: a thread with no generated title unfurls as the
    // default rather than exposing the user's raw query to crawlers.
    if (title) {
        const { actualQuery } = extractQuoteFromQuery(title);
        title = actualQuery || title;
        title = truncateText(title, TITLE_MAX_LENGTH);
    }
    const description = parsed.answer
        ? truncateText(parsed.answer, DESCRIPTION_MAX_LENGTH)
        : DEFAULT_DESCRIPTION;
    const canonicalURL = firstEntity?.thread_url_slug
        ? canonicalizeLegacyPermalinkUrlText(isArticlePage(firstEntity)
            ? `https://www.perplexity.ai/page/${firstEntity.thread_url_slug}`
            : `https://www.perplexity.ai/search/${firstEntity.thread_url_slug}`)
        : undefined;
    const cleanedDescription = cleanPPLXActionLinks(description);
    return generatePageMetadataSync({
        imageURL,
        title,
        description: cleanedDescription,
        author,
        readTime,
        datePublished: firstEntity?.updated_datetime,
        canonicalURL,
        index: false,
        userAgent,
    });
};
const trackThreadOGImageServing = ({ result, slug, userAgent, imageURL, }) => {
    const tag = '[trackThreadOGImageServing]';
    try {
        // filter noisy logs
        if (slug !== 'new') {
            // Callers may deliberately withhold `slug`/`userAgent` (see the
            // public-share gate); omit them rather than logging "undefined".
            const context = [slug, userAgent].filter(Boolean).map((part) => `[${part}] `);
            logWithTime(`${tag} ${context.join('')}${result}${imageURL ? `: ${imageURL}` : ''}`);
        }
    }
    catch (err) {
        warnWithTime('[trackThreadOGImageServing] error:', {
            result,
            slug,
            userAgent,
            imageURL,
            err,
        });
    }
};
const getThreadOGImageURL = (firstResult) => {
    const defaultURL = PERPLEXITY_DEFAULT_SHARE_LOGO_URL;
    const tag = `[getThreadOGImageURL] [${firstResult?.thread_url_slug}]`;
    try {
        const isPage = isArticlePage(firstResult);
        if (firstResult.thread_url_slug) {
            if (isPage) {
                return `https://www.perplexity.ai/api/og/page/${firstResult.thread_url_slug}`;
            }
            else {
                return `https://www.perplexity.ai/api/og/thread/${firstResult.thread_url_slug}`;
            }
        }
        return defaultURL;
    }
    catch (error) {
        logWithTime(`${tag} error getting thread og image url: `, error);
    }
    return defaultURL;
};
/**
 * @testonly
 */
export const getSpaceOGImageURL = (collection) => {
    const defaultURL = PERPLEXITY_DEFAULT_SHARE_LOGO_URL;
    const tag = `[getSpaceOGImageURL] [${collection?.slug}]`;
    try {
        if (collection?.slug) {
            return `https://www.perplexity.ai/api/og/space/${collection?.slug}`;
        }
        return defaultURL;
    }
    catch (error) {
        logWithTime(`${tag} error getting space og image url: `, error);
    }
    return defaultURL;
};
export const generatePageMetadataSync = ({ title = DEFAULT_TITLE, description = DEFAULT_DESCRIPTION, imageURL = PERPLEXITY_DEFAULT_SHARE_LOGO_URL, canonicalURL, author, datePublished, index, userAgent, colorScheme, faviconHref = DEFAULT_FAVICON_HREF, gatedUserContent = false, }) => {
    try {
        let windowsApp = false;
        if (typeof window === 'undefined') {
            windowsApp = userAgent ? isWindowsApp(userAgent) : false;
        }
        if (windowsApp) {
            title = DEFAULT_TITLE;
        }
        const date = YYYYMMDD(datePublished);
        // A gated preview must not echo the requested path: a legacy permalink
        // embeds the withheld title.
        canonicalURL = gatedUserContent ? PERPLEXITY_SITE_URL : (canonicalURL ?? './');
        return {
            metadataBase: new URL('https://www.perplexity.ai'),
            ...(gatedUserContent ? { gatedUserContent: true } : {}),
            title,
            description,
            author: author ?? undefined,
            colorScheme,
            icons: {
                icon: faviconHref,
            },
            alternates: {
                canonical: canonicalURL,
            },
            openGraph: {
                url: canonicalURL,
                images: [imageURL],
                locale: 'en_US',
                type: 'website',
                siteName: 'Perplexity AI',
            },
            twitter: {
                card: description ? 'summary_large_image' : 'summary',
                images: [imageURL],
                site: '@perplexity_ai',
                creator: author ? `@${author}` : undefined,
            },
            other: {
                datePublished: date ?? '',
            },
            robots: {
                index,
            },
        };
    }
    catch (error) {
        // Log the error but return a safe fallback
        warnWithTime('[generatePageMetadata] Error generating metadata:', error);
        return {
            title: DEFAULT_TITLE,
            description: DEFAULT_DESCRIPTION,
            metadataBase: new URL('https://www.perplexity.ai'),
            colorScheme,
            icons: {
                icon: faviconHref,
            },
            robots: {
                index: false,
            },
        };
    }
};
export function generateComputerPageMetadataSync({ title, description, userAgent, faviconHref, }) {
    return generatePageMetadataSync({
        index: true,
        title,
        description: description ?? COMPUTER_PAGE_DESCRIPTION,
        imageURL: COMPUTER_PAGE_IMAGE_URL,
        faviconHref,
        userAgent,
    });
}
export function generateSpaceMetadataSync({ canonicalPathPrefix = 'spaces', collection, slug, userAgent, }) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY_MIGRATION: Please fix in passing
    if (!slug || !collection || collection?.status === 'failed') {
        return generatePageMetadataSync({
            index: false,
            userAgent,
        });
    }
    const projectTypeName = canonicalPathPrefix === 'projects' ? 'Project' : 'Space';
    return generatePageMetadataSync({
        index: false,
        title: collection?.title ?? 'Perplexity AI',
        description: `A ${projectTypeName} on Perplexity AI by ` +
            collection?.owner_user?.username +
            ' — ' +
            collection?.description,
        imageURL: getSpaceOGImageURL(collection),
        canonicalURL: `https://www.perplexity.ai/${canonicalPathPrefix}/${slug}`,
        author: collection?.owner_user?.username,
        datePublished: collection?.updated_datetime,
        userAgent,
    });
}
export function generateArticlePageMetadataSync({ articleSection, slug, userAgent, audience, }) {
    if (!slug || !articleSection) {
        return generateThreadPageMetadataSync({
            slug,
            userAgent,
            audience,
        });
    }
    return generateThreadPageMetadataSync({
        firstEntity: articleSection,
        slug,
        userAgent,
        audience,
    });
}
export function generateAcademicMetadataSync({ userAgent }) {
    return generatePageMetadataSync({
        imageURL: 'https://r2cdn.perplexity.ai/pplx-academic-og.png',
        title: 'Perplexity Academic',
        description: 'The free, AI-powered academic search engine for scholars, students, and educators. Get instant answers from research papers, peer-reviewed journal articles, theses, conference papers, technical reports, and more.',
        index: true,
        userAgent,
    });
}
export function generatePatentsMetadataSync({ userAgent }) {
    return generatePageMetadataSync({
        title: 'Perplexity Patents',
        index: true,
        description: 'Explore patent information and innovation with Perplexity Patents',
        userAgent,
    });
}
export function generatePerplexifyMeMetadataSync({ userAgent }) {
    return generatePageMetadataSync({
        title: 'Perplexify Me',
        index: true,
        imageURL: 'https://r2cdn.perplexity.ai/poster-lab-og-image.png',
        description: 'Generate a personalized Perplexity poster.',
        userAgent,
    });
}
export function generateDiscoverMetadataSync({ userAgent }) {
    return generatePageMetadataSync({
        title: 'Perplexity Discover',
        index: true,
        imageURL: 'https://r2cdn.perplexity.ai/pplx-discover-og.png',
        description: 'Stay informed on the biggest stories happening around the world across tech, finance, sports, entertainment and more with Perplexity Discover.',
        userAgent,
    });
}
function generateFinanceDefaultMetadataSync({ $t, userAgent, }) {
    return generatePageMetadataSync({
        title: $t({
            defaultMessage: 'Perplexity Finance',
            id: 'bec81d2be8',
            description: 'Page title for Perplexity Finance',
        }),
        description: $t({
            defaultMessage: 'Get the latest stock price, historical data, earnings reports, analyst ratings, and forecasts from Perplexity Finance.',
            id: 'e9f6b41bd9',
            description: 'Page description for Perplexity Finance',
        }),
        imageURL: 'https://r2cdn.perplexity.ai/finance/images/og-finance-generic.jpg',
        index: true,
        userAgent,
    });
}
function generateFinanceIndexMetadataSync({ $t, userAgent, section, uuid, financeRouteData, }) {
    if (uuid) {
        if (section === 'predictions' && financeRouteData?.predictionEventData) {
            return generatePredictionsMetadataSync({
                $t,
                userAgent,
                predictionEventData: financeRouteData.predictionEventData,
            });
        }
    }
    if (section === 'politicians' && financeRouteData?.politicianData) {
        return generatePoliticiansMetadataSync({
            $t,
            userAgent,
            politicianData: financeRouteData.politicianData,
        });
    }
    if (section === 'news' && financeRouteData?.newsDocumentData) {
        return generateNewsMetadataSync({
            $t,
            userAgent,
            newsDocumentData: financeRouteData.newsDocumentData,
        });
    }
    return generateFinanceDefaultMetadataSync({
        $t,
        userAgent,
    });
}
function generateFinanceAssetMetadataSync({ userAgent, uuid, tickerSearchData, }) {
    const symbol = uuid ?? tickerSearchData?.symbol;
    const metadata = {
        title: `${symbol} Price: Quote, Forecast, Charts & News`,
        description: `Get the latest ${symbol} price, historical data, charts, analyst ratings, and forecasts from Perplexity Finance.`,
        imageURL: 'https://r2cdn.perplexity.ai/finance/images/og-finance-generic.jpg',
        index: true,
    };
    const tickerMetadata = tickerSearchData ? getFinanceMetadata(tickerSearchData) : {};
    return generatePageMetadataSync({
        ...metadata,
        ...tickerMetadata,
        userAgent,
    });
}
function generatePoliticiansMetadataSync({ $t, userAgent, politicianData, }) {
    if (politicianData && politicianData.name) {
        return generatePageMetadataSync({
            title: `${politicianData.name}'s Trading Activity • Perplexity Finance`,
            description: $t({
                defaultMessage: 'Track the trades and portfolios of members of the House and Senate on Perplexity Finance.',
                id: '3692e43a95',
                description: 'Page description for politician trading activity pages',
            }),
            imageURL: 'https://r2cdn.perplexity.ai/finance/images/og-finance-generic.jpg',
            index: true,
            userAgent,
        });
    }
    return generateFinanceDefaultMetadataSync({
        $t,
        userAgent,
    });
}
function generateNewsMetadataSync({ $t, userAgent, newsDocumentData, }) {
    if (newsDocumentData && newsDocumentData.title) {
        return generatePageMetadataSync({
            title: newsDocumentData.title,
            description: $t({
                defaultMessage: 'Stay updated on market news, company earnings, insider activity, and analyst price target changes with Perplexity Finance.',
                id: '42198a94eb',
                description: 'Page description for financial news articles',
            }),
            imageURL: 'https://r2cdn.perplexity.ai/finance/images/og-finance-generic.jpg',
            index: true,
            userAgent,
        });
    }
    return generateFinanceDefaultMetadataSync({
        $t,
        userAgent,
    });
}
function generatePredictionsMetadataSync({ $t, userAgent, predictionEventData, }) {
    if (predictionEventData && predictionEventData.title) {
        return generatePageMetadataSync({
            title: predictionEventData.title,
            description: $t({
                defaultMessage: 'Track live prediction market odds, implied probabilities, and market moves with Perplexity Finance.',
                id: 'dbca126381',
                description: 'Page description for prediction market event pages',
            }),
            imageURL: 'https://r2cdn.perplexity.ai/finance/images/og-finance-generic.jpg',
            index: true,
            userAgent,
        });
    }
    return generateFinanceDefaultMetadataSync({
        $t,
        userAgent,
    });
}
const stockMetadata = (tickerSearchData) => {
    return {
        title: `${tickerSearchData.name} Stock Price: Quote, Forecast, Splits & News (${tickerSearchData.symbol})`,
        description: `Get the latest ${tickerSearchData.name} stock price (${tickerSearchData.symbol}), historical data, earnings reports, analyst ratings, and forecasts from Perplexity Finance.`,
    };
};
const cryptoMetadata = (tickerSearchData) => {
    return {
        title: `${tickerSearchData.name} Price: Quote, Market Cap, Forecast & News (${tickerSearchData.symbol})`,
        description: `Get the latest ${tickerSearchData.name} price ${tickerSearchData.symbol}, real-time quotes, market cap, historical data, charts, price predictions, and news from Perplexity Finance.`,
    };
};
const forexMetadata = (tickerSearchData) => {
    return {
        title: `${tickerSearchData.name} Exchange Rate: Live Price, Chart, Forecast & News`,
        description: `Get the latest ${tickerSearchData.name} exchange rate, live price, interactive chart, historical data, and expert forecast from Perplexity Finance.`,
    };
};
const commodityMetadata = (tickerSearchData) => {
    return {
        title: `${tickerSearchData.name} Price: Live Chart, Forecast & News (${tickerSearchData.symbol})`,
        description: `Get the latest ${tickerSearchData.name} price (${tickerSearchData.symbol}), live charts, market trends, expert forecasts, and breaking news from Perplexity Finance.`,
    };
};
const fallbackMetadata = (tickerSearchData) => {
    return {
        title: `${tickerSearchData.name} Price: Quote, Forecast, Charts & News (${tickerSearchData.symbol})`,
        description: `Get the latest ${tickerSearchData.name} price ${tickerSearchData.symbol}, historical data, charts, analyst ratings, and forecasts from Perplexity Finance.`,
    };
};
const getFinanceMetadata = (tickerSearchData) => {
    if (tickerSearchData.exchange === 'NASDAQ' ||
        tickerSearchData.exchange === 'NYSE' ||
        tickerSearchData.exchange === 'AMEX') {
        return stockMetadata(tickerSearchData);
    }
    else if (tickerSearchData.exchange === 'CRYPTO') {
        return cryptoMetadata(tickerSearchData);
    }
    else if (tickerSearchData.exchange === 'FOREX') {
        return forexMetadata(tickerSearchData);
    }
    else if (tickerSearchData.exchange === 'COMMODITY') {
        return commodityMetadata(tickerSearchData);
    }
    else {
        return fallbackMetadata(tickerSearchData);
    }
};
export function generateFinanceMetadataSync({ pathname, financeRouteData, userAgent, $t, }) {
    const r = parseFinanceRoute(pathname);
    if (r.type === 'index') {
        return generateFinanceIndexMetadataSync({
            $t,
            userAgent,
            section: r.section,
            uuid: r.uuid,
            financeRouteData: financeRouteData ?? {},
        });
    }
    if (r.type === 'asset' && r.uuid) {
        return generateFinanceAssetMetadataSync({
            userAgent,
            uuid: r.uuid,
            tickerSearchData: financeRouteData?.tickerSearchData ?? undefined,
        });
    }
    return generateFinanceDefaultMetadataSync({
        $t,
        userAgent,
    });
}
export function generateShortcutSettingsMetadataSync({ shortcut, }) {
    if (!shortcut) {
        return generatePageMetadataSync({
            index: false,
            title: 'Perplexity Account',
        });
    }
    return generatePageMetadataSync({
        index: false,
        title: truncateText(`/${shortcut.task_name}`, TITLE_MAX_LENGTH),
        description: truncateText(shortcut.prompt, DESCRIPTION_MAX_LENGTH),
        imageURL: `https://www.perplexity.ai/api/og/shortcut/${shortcut.task_name}`,
    });
}
export function generateGrowCometMetadataSync() {
    return generatePageMetadataSync({
        index: true,
        title: 'The browser that works as fast as you think',
        description: 'Comet is an AI-powered browser that acts as a personal assistant and thinking partner.',
        imageURL: 'https://pplx-res.cloudinary.com/image/upload/t_cometemail/comet_invites/vrpO7vvCJMg0vJrvkht69_78c80f5620434b53a04b53d23622143d_fit2ix.png',
    });
}
export function generateStudentLandingMetadataSync() {
    return generatePageMetadataSync({
        index: true,
        title: 'Get Perplexity Pro for Students for $10/mo',
        description: 'Access every model in one subscription, get accurate answers with inline citations, and use Computer to get work done end-to-end.',
        imageURL: r2Asset('growth/students/student-landing-library-og.jpg'),
    });
}
export function generateGrowStudentCometMetadataSync() {
    return generatePageMetadataSync({
        index: true,
        title: 'Get Comet with discounted Perplexity Pro. The AI browser built for students.',
        description: 'Verify your student status to unlock access to Comet and Perplexity Pro at a discount',
        imageURL: 'https://r2cdn.perplexity.ai/comet/cosmic_student_studying.jpg',
    });
}
export async function generateBrowserClaimMetadataSync({ code, }) {
    let image_url;
    try {
        const { data, error } = await browserInvitesClient.GET('/rest/browser/invites/og_image', 'og_image_comet_invite', {
            params: {
                query: {
                    invite_code: code,
                },
            },
            timeoutMs: 5000,
        });
        if (error) {
            throw error;
        }
        image_url = data.image_url;
    }
    catch {
        // ignore
    }
    if (!image_url) {
        image_url =
            'https://pplx-res.cloudinary.com/image/upload/t_cometemail/comet_invites/vrpO7vvCJMg0vJrvkht69_78c80f5620434b53a04b53d23622143d_fit2ix.png';
    }
    return generatePageMetadataSync({
        index: false,
        title: "You're invited to Comet",
        description: 'Claim your exclusive access to the AI powered browser by Perplexity.',
        imageURL: image_url,
    });
}
export function generateBrowserClaimInviteMetadataSync({ userAgent, }) {
    return generatePageMetadataSync({
        title: 'A personal AI assistant for the web',
        index: true,
        imageURL: 'https://r2cdn.perplexity.ai/grow_comet_no_pro_compressed_preview_img.jpg',
        description: 'Browse smarter, faster, and with less friction.',
        userAgent,
    });
}
export function generateMaxMetadataSync() {
    return generatePageMetadataSync({
        index: true,
        title: 'Perplexity Max',
        description: 'Perplexity Max is our most advanced subscription tier yet, built for those who demand peak AI productivity and early access to the newest products.',
    });
}
export function generateProMetadataSync() {
    return generatePageMetadataSync({
        index: true,
        title: 'Perplexity Pro',
        description: 'Perplexity Pro unlocks more ways to learn and get work done. Upgrade your productivity and learning with access to the latest AI models, extended deep research, increased file uploads and deeper sourcing.',
        imageURL: PERPLEXITY_PRO_SUMMARY_SHARE_URL,
    });
}
//# sourceMappingURL=metadata.js.map